// AdminCreateOrderPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  Input,
  FormControl,
  FormLabel,
  VStack,
  useToast,
  Tag,
  TagLabel,
  Divider,
  Alert,
  AlertIcon,
  Select as CSelect,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  RadioGroup,
  Radio,
  HStack,
} from '@chakra-ui/react';
import { useCognito } from '../utils/useCognito';
import {
  getProductList,
  adminCreateOrderForUser,
  getBaaDataForUser,
  adminUpdateOrderForUser,
} from '../utils/commonApiCalls';
import AdminGraftSelectorPro from '../components/AdminGraftSelectorPro';

const AdminCreateOrderPage = () => {
  // --- Routing params ---
  const { ivrId: routeIvrId, userId } = useParams();
  const [searchParams] = useSearchParams();
  const isTestMode = searchParams.get('test') === 'true';
  const cloneOrderId = searchParams.get('cloneOrderId');
  const editOrderId = searchParams.get('editOrderId');
  const powerParam = searchParams.get('power') === '1';

  // --- New: Order “source” mode ---
  // - from-ivr      => existing / normal flow (IVR-backed)
  // - subsequent    => future: re-order / next application based on previous order/patient
  // - standalone    => future: not linked to IVR or previous order (disabled in UI)
  const [orderMode, setOrderMode] = useState(() => 'from-ivr');

  // The IVR we’re actually using (may or may not come from the route)
  const [activeIvrId, setActiveIvrId] = useState(routeIvrId || '');
  const [ivr, setIvr] = useState(null);

  // For searchable IVR list (when launched stand-alone)
  const [allIvrs, setAllIvrs] = useState([]);
  const [ivrSearch, setIvrSearch] = useState('');
  const [isLoadingIvrs, setIsLoadingIvrs] = useState(false);

  // Recent per-patient orders (for subsequent mode pre-IVR selection)
  const [recentPatientOrders, setRecentPatientOrders] = useState([]); // [{patientName, order}]
  const [loadingRecentPatientOrders, setLoadingRecentPatientOrders] = useState(false);
  const [recentPatientOrdersError, setRecentPatientOrdersError] = useState(null);
  // Search within recent patient orders (subsequent mode)
  const [recentSearch, setRecentSearch] = useState('');

  // Mirror TEST_USER_IDS list from OrdersPage for consistent test filtering
  const TEST_USER_IDS = [
    'auth0|67dca56ce48dfa7a186b2b1f',
    'auth0|67cdaa044a714c1f00addefe',
    'auth0|67ef0012b076d56b64707091',
    'auth0|67eebba8f3ef00b630525864',
    'auth0|68081737a26a556b062b0fc2',
    'auth0|67effe3acc3eee3b74316298',
    'auth0|6813d9f226ee7d3fd62139a3',
    'auth0|6838b99566a9c8dfb930f824',
  ];

  // Products & shipping
  const [products, setProducts] = useState([]);
  const [shippingAddresses, setShippingAddresses] = useState([]);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState('');
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [customAddress, setCustomAddress] = useState({
    label: '',
    street: '',
    city: '',
    state: '',
    zip: '',
  });

  const [deliveryDate, setDeliveryDate] = useState('');
  // NOTE: woundMeta now also tracks isRemoved and isNew
  const [woundGrafts, setWoundGrafts] = useState([]); // [{ woundSize, addedGrafts: [], productId?, woundMeta:{...} }]
  const [overrideCreationDate, setOverrideCreationDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [powerMode, setPowerMode] = useState(powerParam);
  const [confirmRemoveIdx, setConfirmRemoveIdx] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();
  const navigate = useNavigate();
  const { getIdToken } = useCognito();
  const decodedUserId = decodeURIComponent(userId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [effectiveUserId, setEffectiveUserId] = useState(decodedUserId);
  const [isBootstrapping, setIsBootstrapping] = useState(!!routeIvrId);

  // --- Keyboard “power mode” toggle ---
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.shiftKey && (e.key === 'M' || e.key === 'm')) {
        setPowerMode((p) => !p);
        toast({
          title: `Admin Power ${powerMode ? 'Off' : 'On'}`,
          status: 'info',
          duration: 1500,
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [powerMode]);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  // Robust fetcher to tolerate different proxy/shape responses
  const fetchAllIvrs = useCallback(
    async (token, testFlag) => {
      const res = await fetch(
        'https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllIvrs',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ test: testFlag }),
        }
      );
      const maybeProxy = await res.json();
      const payload =
        typeof maybeProxy?.body === 'string'
          ? JSON.parse(maybeProxy.body)
          : maybeProxy;

      let allIvrsLocal = [];
      if (payload?.grouped && typeof payload.grouped === 'object') {
        allIvrsLocal = Object.values(payload.grouped).flat();
      } else if (Array.isArray(payload?.items)) {
        allIvrsLocal = payload.items;
      } else if (Array.isArray(payload)) {
        allIvrsLocal = payload;
      }
      return allIvrsLocal;
    },
    []
  );

  // === Core bootstrap for a specific IVR ===
  const bootstrapForIvr = useCallback(
    async (targetIvrId) => {
      if (!targetIvrId) {
        throw new Error('No IVR selected yet.');
      }
      setIsBootstrapping(true);
      const token = getIdToken();

      // ===== IVR load: try current dataset, then the opposite, match by ivrId =====
      let primaryIvrs = await fetchAllIvrs(token, isTestMode);
      let matched = primaryIvrs.find((i) => i.ivrId === targetIvrId);

      if (!matched) {
        const otherIvrs = await fetchAllIvrs(token, !isTestMode);
        matched = otherIvrs.find((i) => i.ivrId === targetIvrId);
        if (matched) {
          toast({
            title: 'Loaded from other dataset',
            description: `IVR ${targetIvrId} was found in ${
              !isTestMode ? 'TEST' : 'PROD'
            } data.`,
            status: 'info',
            duration: 4000,
          });
          primaryIvrs = otherIvrs; // switch to the correct dataset
        }
      }

      if (!matched) {
        throw new Error(
          `Could not find IVR ${targetIvrId} in ${
            isTestMode ? 'TEST' : 'PROD'
          } or the opposite dataset.`
        );
      }

      setActiveIvrId(targetIvrId);
      setIvr(matched);
      setAllIvrs(primaryIvrs);

      // Prefer the IVR's true userId going forward for addresses/permissions
      const userForFetch = matched.userId || decodedUserId;
      if (userForFetch !== decodedUserId) {
        setEffectiveUserId(userForFetch);
        toast({
          title: 'User adjusted',
          description: `IVR belongs to ${userForFetch}. Using that for addresses & permissions.`,
          status: 'info',
          duration: 3500,
        });
      }

      // ===== Products =====
      const productList = await getProductList(token);
      setProducts(productList);

      // ===== Addresses (use effective user for immediate fetch) =====
      const baaData = await getBaaDataForUser(userForFetch, token);
      const addresses = baaData?.shippingAddresses || [];
      setShippingAddresses(addresses);
      if (addresses.length > 0) {
        setSelectedShippingAddress(JSON.stringify(addresses[0]));
      }

      // ===== Default wound bins (existing wounds are NOT removed, NOT new) =====
      const defaultWoundGrafts = (matched.wounds || []).map((w) => ({
        woundSize:
          (parseFloat(w.woundLength) || 0) * (parseFloat(w.woundWidth) || 0),
        addedGrafts: [],
        productId: '',
        woundMeta: {
          woundType: w.woundType,
          woundTypeOther: w.woundTypeOther,
          woundLength: w.woundLength,
          woundWidth: w.woundWidth,
          woundDepth: (w.woundDepth ?? '0').toString(),
          isNew: false,
          isRemoved: false,
        },
      }));

      // ===== Clone/edit -> load order grafts; default isRemoved false =====
      if (cloneOrderId || editOrderId) {
        const orderId = cloneOrderId || editOrderId;
        const orderRes = await fetch(
          'https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllMedicalOrders',
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ orderId }),
          }
        );
        const orderJson = await orderRes.json();
        const order = orderJson?.order;
        if (!order) throw new Error('Order not found');

        setUseCustomAddress(true);
        setCustomAddress(order.shippingAddress);
        setOverrideCreationDate(
          cloneOrderId
            ? new Date().toISOString().slice(0, 10)
            : order.createdAt?.slice(0, 10)
        );
        setDeliveryDate(
          cloneOrderId ? '' : order.deliveryDate?.slice(0, 10)
        );

        const woundMap = {};
        for (const g of order.graftsUsed || []) {
          const idx = g.woundIndex ?? 0;
          if (!woundMap[idx]) {
            woundMap[idx] = {
              woundSize: g.woundSize || 0,
              addedGrafts: [],
              productId: g.productId || g.product || '',
              woundMeta: {
                woundType: g.woundType,
                woundTypeOther: g.woundTypeOther,
                woundLength: g.woundLength,
                woundWidth: g.woundWidth,
                woundDepth: (g.woundDepth ?? '0').toString(),
                isNew: false,
                isRemoved: false,
              },
            };
          }
          woundMap[idx].addedGrafts.push({ ...g });
        }

        const mapped = defaultWoundGrafts.map((wg, idx) =>
          woundMap[idx] ? { ...wg, ...woundMap[idx] } : wg
        );

        setWoundGrafts(mapped);
      } else {
        setWoundGrafts(defaultWoundGrafts);
      }

      setIsBootstrapping(false);
    },
    [
      cloneOrderId,
      editOrderId,
      decodedUserId,
      fetchAllIvrs,
      getIdToken,
      isTestMode,
      toast,
    ]
  );

  // Initial load:
  // - If we have an ivrId from the route => bootstrap directly
  // - Else => just pre-fetch IVRs (for search UI)
  useEffect(() => {
    (async () => {
      try {
        const token = getIdToken();
        if (routeIvrId) {
          await bootstrapForIvr(routeIvrId);
        } else {
          setIsLoadingIvrs(true);
          const ivrs = await fetchAllIvrs(token, isTestMode);
          setAllIvrs(ivrs);
          setIsLoadingIvrs(false);
        }
      } catch (err) {
        console.error('❌ Error loading data:', err);
        toast({
          title: 'Failed to load data',
          description: err.message,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        setIsBootstrapping(false);
        setIsLoadingIvrs(false);
      }
    })();
  }, [routeIvrId, bootstrapForIvr, fetchAllIvrs, getIdToken, isTestMode, toast]);

  // === Order Type / Mode helpers ===
  const handleModeChange = (nextMode) => {
    setOrderMode(nextMode);
    // In “subsequent” or “standalone” we might not have an IVR yet.
    // For now, we still require picking an IVR to bootstrap clinical data.
    if (nextMode === 'from-ivr' && !ivr && !activeIvrId && !routeIvrId) {
      toast({
        title: 'Select an IVR',
        description:
          'Search for an IVR below to bootstrap patient and wound details.',
        status: 'info',
        duration: 3000,
      });
    }
    if (nextMode === 'subsequent') {
      toast({
        title: 'Subsequent order mode',
        description:
          'Use the patient/IVR search below to seed this as a subsequent / follow-up order.',
        status: 'info',
        duration: 4000,
      });
      // If no IVR yet, load most recent order per patient as starting points
      if (!ivr) {
        fetchMostRecentOrdersByPatient();
      }
    }
  };

  // Fetch newest order per patient (unique patient name heuristic)
  const fetchMostRecentOrdersByPatient = useCallback(async () => {
    try {
      setLoadingRecentPatientOrders(true);
      setRecentPatientOrdersError(null);
      const token = getIdToken();
      const res = await fetch(
        'https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllMedicalOrders',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ test: isTestMode }),
        }
      );
      const data = await res.json();
      const ordersByUser = data?.ordersByUser || {};
      // Apply test vs prod filtering (mirror OrdersPage behavior)
      const filteredOrdersByUser = isTestMode
        ? Object.fromEntries(
            Object.entries(ordersByUser).filter(([uid]) => TEST_USER_IDS.includes(uid))
          )
        : Object.fromEntries(
            Object.entries(ordersByUser).filter(([uid]) => !TEST_USER_IDS.includes(uid))
          );
      // Flatten all orders
      const allOrders = Object.values(filteredOrdersByUser).flat();
      // Group by patient name (lowercased trimmed) heuristic
      const byPatient = {};
      for (const o of allOrders) {
        const rawName = (o.patientName || 'Unknown Patient').trim();
        const key = rawName.toLowerCase();
        if (!byPatient[key]) byPatient[key] = [];
        byPatient[key].push(o);
      }
      // Pick newest per patient
      const newestPerPatient = Object.entries(byPatient).map(([key, orders]) => {
        const sorted = [...orders].sort(
          (a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
        );
        const top = sorted[0];
        return { patientName: top?.patientName || 'Unknown Patient', order: top };
      });

      // Collect unique userIds from newest orders to fetch facility data
      const uniqueUserIds = Array.from(new Set(newestPerPatient.map(o => o.order?.userId).filter(Boolean)));
      const facilityMap = {};
      const physicianMap = {};
      for (const uid of uniqueUserIds) {
        try {
          const baa = await getBaaDataForUser(uid, token);
          facilityMap[uid] = baa?.facility?.name || baa?.facilityName || baa?.facilityAddress?.name || '';
          const physicianName = baa?.physicianInfo?.name || baa?.physicianInfo?.physicianName || '';
          physicianMap[uid] = physicianName;
        } catch (e) {
          console.warn('Facility fetch failed for', uid, e);
          facilityMap[uid] = '';
          physicianMap[uid] = '';
        }
      }
      const sortedFinal = newestPerPatient
        .filter((o) => !!o.order)
        .map(o => ({ ...o, facilityName: facilityMap[o.order.userId] || '', physicianName: physicianMap[o.order.userId] || '' }))
        .sort(
          (a, b) => new Date(b.order.updatedAt || b.order.createdAt || 0) - new Date(a.order.updatedAt || a.order.createdAt || 0)
        );
      setRecentPatientOrders(sortedFinal);
    } catch (err) {
      console.error('Failed to fetch recent user orders', err);
      setRecentPatientOrdersError(err.message || 'Failed loading recent orders');
    } finally {
      setLoadingRecentPatientOrders(false);
    }
  }, [getIdToken, isTestMode]);

  // Filter recent patient orders by search (patient, facility, physician, orderId, ivrId)
  const filteredRecentPatientOrders = useMemo(() => {
    if (!recentSearch) return recentPatientOrders;
    const q = recentSearch.toLowerCase();
    return recentPatientOrders.filter(({ patientName, facilityName, physicianName, order }) => {
      const patient = (patientName || '').toLowerCase();
      const facility = (facilityName || '').toLowerCase();
      const physician = (physicianName || '').toLowerCase();
      const orderId = String(order?.orderId || '').toLowerCase();
      const ivrId = String(order?.ivrId || '').toLowerCase();
      return (
        patient.includes(q) ||
        facility.includes(q) ||
        physician.includes(q) ||
        orderId.includes(q) ||
        ivrId.includes(q)
      );
    });
  }, [recentSearch, recentPatientOrders]);

  // Selecting a recent order pre-IVR: attempt to bootstrap using its ivrId if present
  const handleSelectRecentOrder = async (order) => {
    if (!order) return;
    // If order has ivrId, bootstrap directly; otherwise require manual IVR search
    if (order.ivrId) {
      try {
        await bootstrapForIvr(order.ivrId);
        // Pre-fill shipping address and grafts (copy logic)
        if (order.shippingAddress) {
          setUseCustomAddress(true);
          setCustomAddress(order.shippingAddress);
        }
        // Map grafts into woundGrafts (similar to clone behavior)
        const woundMap = {};
        for (const g of order.graftsUsed || []) {
          const idx = g.woundIndex ?? 0;
          if (!woundMap[idx]) {
            woundMap[idx] = {
              woundSize: g.woundSize || 0,
              addedGrafts: [],
              productId: g.productId || g.product || '',
              woundMeta: {
                woundType: g.woundType,
                woundTypeOther: g.woundTypeOther,
                woundLength: g.woundLength,
                woundWidth: g.woundWidth,
                woundDepth: (g.woundDepth ?? '0').toString(),
                isNew: false,
                isRemoved: false,
              },
            };
          }
          woundMap[idx].addedGrafts.push({ ...g });
        }
        setWoundGrafts(Object.values(woundMap));
        toast({ title: 'Loaded prior patient order', description: `Copied contents from ${order.orderId}`, status: 'success', duration: 2500 });
      } catch (err) {
        toast({ title: 'Bootstrap failed', description: err.message, status: 'error' });
      }
    } else {
      toast({ title: 'Order has no IVR ID', description: 'Select an IVR manually to continue.', status: 'info' });
    }
  };

  // Searchable IVR list (for “Order from IVR” or “Subsequent Order” with no route ivrId)
  const filteredIvrs = useMemo(() => {
    if (!ivrSearch) return allIvrs.slice(0, 25); // top 25 for sanity
    const q = ivrSearch.toLowerCase();
    return allIvrs
      .filter((i) => {
        const pName = i.patient?.name?.toLowerCase() || '';
        const fName = i.facility?.name?.toLowerCase() || '';
        const id = i.ivrId?.toLowerCase() || '';
        return (
          pName.includes(q) || fName.includes(q) || id.includes(q)
        );
      })
      .slice(0, 25);
  }, [allIvrs, ivrSearch]);

  const addWound = () => {
    setWoundGrafts((prev) => [
      ...prev,
      {
        woundSize: 0,
        addedGrafts: [],
        productId: products[0]?.productId || '',
        woundMeta: {
          woundType: 'Other',
          woundTypeOther: '',
          woundLength: '0',
          woundWidth: '0',
          woundDepth: '0',
          isNew: true,
          isRemoved: false,
        },
      },
    ]);
  };

  // OPEN confirm modal for the wound at idx
  const confirmRemoveWound = (idx) => {
    setConfirmRemoveIdx(idx);
    onOpen();
  };

  // finalize removal:
  // - if isNew => delete from list
  // - else => mark isRemoved=true (gray out; can restore)
  const finalizeRemoveWound = () => {
    setWoundGrafts((prev) => {
      const copy = [...prev];
      const current = copy[confirmRemoveIdx];
      if (!current) return prev;

      if (current.woundMeta?.isNew) {
        copy.splice(confirmRemoveIdx, 1);
      } else {
        copy[confirmRemoveIdx] = {
          ...current,
          woundMeta: { ...(current.woundMeta || {}), isRemoved: true },
        };
      }
      return copy;
    });
    setConfirmRemoveIdx(null);
    onClose();
  };

  const restoreWound = (idx) => {
    setWoundGrafts((prev) => {
      const copy = [...prev];
      if (!copy[idx]) return prev;
      copy[idx] = {
        ...copy[idx],
        woundMeta: { ...(copy[idx].woundMeta || {}), isRemoved: false },
      };
      return copy;
    });
  };

  const setWoundMeta = (idx, patch) => {
    setWoundGrafts((prev) => {
      const clone = [...prev];
      const wm = { ...(clone[idx].woundMeta || {}), ...patch };
      clone[idx].woundMeta = wm;
      const wl = parseFloat(wm.woundLength) || 0;
      const ww = parseFloat(wm.woundWidth) || 0;
      clone[idx].woundSize = wl * ww;
      return clone;
    });
  };

  const addGraft = (woundIdx, graft) => {
    setWoundGrafts((prev) => {
      const clone = [...prev];
      const wound = clone[woundIdx];
      const existing = wound.addedGrafts.find(
        (g) => g.productId === graft.productId && g.size === graft.size
      );
      if (existing) {
        existing.quantity = (parseInt(existing.quantity) || 0) + 1;
      } else {
        wound.addedGrafts.push({
          ...graft,
          quantity: parseInt(graft.quantity) || 1,
        });
      }
      return clone;
    });
  };

  const removeGraft = (woundIdx, graft) => {
    setWoundGrafts((prev) => {
      const clone = [...prev];
      clone[woundIdx].addedGrafts = clone[woundIdx].addedGrafts.filter(
        (g) =>
          !(
            g.productId === graft.productId &&
            g.size === graft.size &&
            g.quantity === graft.quantity
          )
      );
      return clone;
    });
  };

  const setWoundProduct = (idx, productId) => {
    setWoundGrafts((prev) => {
      const clone = [...prev];
      clone[idx].productId = productId;
      return clone;
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // guard against double-clicks
    if (!ivr && orderMode === 'from-ivr') {
      toast({
        title: 'IVR not selected',
        description: 'Please select an IVR before submitting this order.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }

    setIsSubmitting(true);

    const effectiveIvrId = ivr?.ivrId || activeIvrId || routeIvrId || null;

    const payload = {
      ...(effectiveIvrId ? { ivrId: effectiveIvrId } : {}),
      userId: effectiveUserId,
      deliveryDate,
      overrideCreationDate,
      shippingAddress: useCustomAddress
        ? customAddress
        : JSON.parse(selectedShippingAddress || '{}'),
      graftsUsed: (woundGrafts || [])
        .map((wg, woundIndex) => ({ wg, woundIndex }))
        .filter(({ wg }) => !wg.woundMeta?.isRemoved)
        .flatMap(({ wg, woundIndex }) =>
          (wg.addedGrafts || []).map((g) => ({
            ...g,
            woundIndex,
            woundSize: wg.woundSize,
            woundType: wg.woundMeta?.woundType,
            woundTypeOther:
              wg.woundMeta?.woundType === 'Other'
                ? wg.woundMeta?.woundTypeOther || ''
                : undefined,
            woundLength: wg.woundMeta?.woundLength,
            woundWidth: wg.woundMeta?.woundWidth,
            woundDepth: wg.woundMeta?.woundDepth,
          }))
        ),
      // Future: you can tag orderMode here if you want to persist it
      // orderMode,
    };

    try {
      const token = getIdToken();

      if (editOrderId) {
        const res = await adminUpdateOrderForUser(
          { ...payload, orderId: editOrderId },
          token
        );
        if (res.success) {
          toast({ title: 'Order Updated', status: 'success' });
          navigate(
            `/admin/order-success?orderId=${editOrderId}${
              effectiveIvrId ? `&ivrId=${effectiveIvrId}` : ''
            }&action=edit`
          );
        } else {
          toast({
            title: res.error || 'Order update failed',
            status: 'error',
          });
        }
      } else {
        const res = await adminCreateOrderForUser(payload, token);
        if (res.success) {
          toast({ title: 'Order Created', status: 'success' });
          navigate(
            `/admin/order-success?orderId=${res.orderId}${
              effectiveIvrId ? `&ivrId=${effectiveIvrId}` : ''
            }&action=create`
          );
        } else {
          toast({
            title: res.error || 'Order creation failed',
            status: 'error',
          });
        }
      }
    } catch (err) {
      toast({ title: 'Order error', status: 'error' });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // map productId -> price (fallback-safe)
  const productPriceMap = useMemo(() => {
    const map = {};
    (products || []).forEach((p) => {
      const id = p.productId ?? p.id;
      const price = parseFloat(p.price ?? p.Price ?? 0);
      if (id) map[id] = isNaN(price) ? 0 : price;
    });
    return map;
  }, [products]);

  // live totals (skip removed wounds)
  const orderTotals = useMemo(() => {
    let totalList = 0;
    let totalArea = 0;

    (woundGrafts || []).forEach((wg) => {
      if (wg?.woundMeta?.isRemoved) return; // don't count removed wounds
      (wg.addedGrafts || []).forEach((g) => {
        const [aw, ah] = String(g.size || '')
          .toLowerCase()
          .split('x')
          .map(Number);
        const qty = parseInt(g.quantity);
        if (!isNaN(aw) && !isNaN(ah) && !isNaN(qty)) {
          const area = aw * ah * qty;
          const unitPrice = parseFloat(
            g.productData?.price ?? productPriceMap[g.productId] ?? 0
          );
          totalArea += area;
          totalList += area * (isNaN(unitPrice) ? 0 : unitPrice);
        }
      });
    });

    const expectedPayout = totalList * 0.6; // -40%
    return { totalList, expectedPayout, totalArea };
  }, [woundGrafts, productPriceMap]);

  const renderIvrDetails = () => {
    if (!ivr) return null;
    const { patient, physician, facility, status, posType, formType } = ivr;
    return (
      <Box mb={6} p={4} borderWidth={1} borderRadius="md" bg="gray.50">
        <Heading size="md" mb={2}>
          IVR Summary
        </Heading>
        <Text>
          <strong>Status:</strong> {status}
        </Text>
        <Text>
          <strong>Form Type:</strong> {formType}
        </Text>
        <Divider my={2} />
        <Text fontWeight="bold">Patient Info</Text>
        <Text>
          {patient?.name}, DOB: {patient?.dob}, Phone: {patient?.phone}
        </Text>
        <Text>{patient?.address}</Text>
        <Divider my={2} />
        <Text fontWeight="bold">Physician Info</Text>
        <Text>
          {physician?.name}, NPI: {physician?.npi}, TIN: {physician?.tin}
        </Text>
        <Text>
          {physician?.address}, Phone: {physician?.phone}
        </Text>
        <Divider my={2} />
        <Text fontWeight="bold">Facility Info</Text>
        <Text>{facility?.name}</Text>
        <Text>
          {facility?.address}, Phone: {facility?.phone}
        </Text>
        <Text>
          NPI: {facility?.npi}, TIN: {facility?.tin}
        </Text>
        <Text>POS: {posType}</Text>
      </Box>
    );
  };

  const headingLabel = useMemo(() => {
    const baseName = ivr?.patient?.name || 'Patient';
    if (cloneOrderId) return `Clone Order for ${baseName}`;
    if (editOrderId) return `Edit Order for ${baseName}`;
    if (orderMode === 'subsequent') return `Subsequent Order for ${baseName}`;
    return `Create Order for ${baseName}`;
  }, [ivr, cloneOrderId, editOrderId, orderMode]);

  const orderReady = !!ivr; // For now, we still hinge on IVR to drive clinical details

  return (
    <Box p={8} maxW="1000px" mx="auto">
      <Heading mb={4}>
        {headingLabel}
        {isTestMode && ' (Test Mode)'}
      </Heading>

      {cloneOrderId && (
        <Alert status="info" mt={4}>
          <AlertIcon />
          You are cloning order {cloneOrderId}. Please review details before
          submitting.
        </Alert>
      )}
      {editOrderId && (
        <Alert status="warning" mt={4}>
          <AlertIcon />
          You are editing an existing order ({editOrderId}). Changes will
          overwrite the original.
        </Alert>
      )}

      {/* Order Mode Selector */}
      <Box mt={4} mb={4} p={3} borderWidth={1} borderRadius="md" bg="white" borderColor="gray.200" boxShadow="sm">
        <Text fontSize="sm" mb={2} color="gray.700">
          What kind of order are we creating?
        </Text>
        <RadioGroup
          value={orderMode}
          onChange={handleModeChange}
        >
          <HStack spacing={6}>
            <Radio value="from-ivr" colorScheme="teal">
              Order from IVR
            </Radio>
            <Radio value="subsequent" colorScheme="teal">
              Subsequent Order (follow-up)
            </Radio>
            <Radio value="standalone" isDisabled colorScheme="teal">
              Stand-alone Order (coming soon)
            </Radio>
          </HStack>
        </RadioGroup>
        {orderMode === 'subsequent' && (
          <Text fontSize="xs" color="gray.600" mt={1}>
            For now, subsequent orders still bootstrap from an IVR. In the next
            iteration, we can drive this directly off patient/order history.
          </Text>
        )}
      </Box>

      {/* Hidden power hint */}
      <Box mt={2} mb={4}>
        {powerMode ? (
          <Tag colorScheme="purple">
            <TagLabel>Admin Power: Mix Brands Enabled</TagLabel>
          </Tag>
        ) : (
          <Text fontSize="xs" color="gray.500">
            (press <strong>Alt+Shift+M</strong> or add <code>?power=1</code> to
            URL for per-wound brand control)
          </Text>
        )}
      </Box>

      {/* IVR Search / Selection (only if we don't already have ivr from route/bootstrap) */}
      {!orderReady && (
        <Box mb={6} p={4} borderWidth={1} borderRadius="md" bg="white" borderColor="gray.200" boxShadow="sm">
          <Heading size="sm" mb={3} color="gray.800">
            {orderMode === 'subsequent'
              ? 'Find a prior IVR to base this subsequent order on'
              : 'Select an IVR to create an order from'}
          </Heading>

          {orderMode === 'subsequent' && (
            <Box mb={4}>
              <Heading size="xs" mb={2} color="purple.700">Most Recent Order Per Patient</Heading>
              <Flex gap={2} mb={2} wrap="wrap" align="center">
                <Input
                  size="xs"
                  placeholder="Search patient, facility, physician, order or IVR"
                  value={recentSearch}
                  onChange={(e) => setRecentSearch(e.target.value)}
                  maxW="320px"
                  bg="white"
                />
                {recentSearch && (
                  <Button size="xs" variant="ghost" onClick={() => setRecentSearch('')} _hover={{ transform: 'scale(1)' }}>Clear</Button>
                )}
              </Flex>
              {loadingRecentPatientOrders && (
                <Flex align="center" gap={2} py={2}><Spinner size="sm" /><Text fontSize="sm" color="gray.600">Loading recent orders…</Text></Flex>
              )}
              {recentPatientOrdersError && (
                <Alert status="error" mb={3} borderRadius="md"><AlertIcon /><Text fontSize="sm">{recentPatientOrdersError}</Text></Alert>
              )}
              {!loadingRecentPatientOrders && !recentPatientOrdersError && recentPatientOrders.length === 0 && (
                <Text fontSize="sm" color="gray.600">No orders found yet.</Text>
              )}
              {!loadingRecentPatientOrders && !recentPatientOrdersError && recentPatientOrders.length > 0 && (
                <Box maxH="240px" overflowY="auto" borderWidth={1} borderColor="purple.200" borderRadius="md" bg="purple.50" p={2} mb={3}>
                  {filteredRecentPatientOrders.length === 0 && (
                    <Text fontSize="xs" color="gray.600" px={1}>No matches for "{recentSearch}"</Text>
                  )}
                  {filteredRecentPatientOrders.map(({ patientName, order, facilityName, physicianName }) => (
                    <Flex key={order.orderId} p={2} mb={1} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" align="center" justify="space-between">
                      <Box minW={0}>
                        <Text fontSize="sm" color="gray.800" noOfLines={1}>Order {order.orderId} <Text as="span" color="gray.500">• {new Date(order.updatedAt || order.createdAt).toLocaleDateString()}</Text></Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>{patientName || 'Unknown Patient'} – {facilityName || 'Unknown Facility'}</Text>
                        {physicianName && <Text fontSize="2xs" color="gray.500" noOfLines={1}>Physician: {physicianName}</Text>}
                      </Box>
                      <Button size="xs" colorScheme="purple" onClick={() => handleSelectRecentOrder(order)} _hover={{ transform: 'scale(1)' }}>Use</Button>
                    </Flex>
                  ))}
                </Box>
              )}
              <Button size="xs" variant="outline" colorScheme="purple" onClick={fetchMostRecentOrdersByPatient} _hover={{ transform: 'scale(1)' }} mb={2}>Refresh Recent Orders</Button>
            </Box>
          )}
          {/* IVR selector removed in subsequent mode; retained for from-ivr */}
          {orderMode === 'from-ivr' && (
            <>
              <Flex gap={2} mb={3} align="center" wrap="wrap">
                <Input
                  placeholder="Search by IVR ID, patient name, or facility"
                  value={ivrSearch}
                  onChange={(e) => setIvrSearch(e.target.value)}
                  maxW="360px"
                  bg="white"
                  size="sm"
                />
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      setIsLoadingIvrs(true);
                      const token = getIdToken();
                      const ivrs = await fetchAllIvrs(token, isTestMode);
                      setAllIvrs(ivrs);
                      setIsLoadingIvrs(false);
                      toast({
                        title: 'IVR list refreshed',
                        status: 'success',
                        duration: 2000,
                      });
                    } catch (err) {
                      console.error(err);
                      setIsLoadingIvrs(false);
                      toast({
                        title: 'Failed to refresh IVRs',
                        status: 'error',
                        duration: 3000,
                      });
                    }
                  }}
                  _hover={{ transform: 'scale(1)' }}
                >
                  Refresh IVRs
                </Button>
              </Flex>
              {isLoadingIvrs ? (
                <Flex align="center" justify="center" py={4}>
                  <Spinner />
                </Flex>
              ) : filteredIvrs.length === 0 ? (
                <Text fontSize="sm" color="gray.600">
                  No IVRs found. Try a different search term.
                </Text>
              ) : (
                <Box
                  maxH="260px"
                  overflowY="auto"
                  borderWidth={1}
                  borderRadius="md"
                  bg="gray.50"
                  p={2}
                  borderColor="gray.200"
                >
                  {filteredIvrs.map((item) => (
                    <Flex
                      key={item.ivrId}
                      justify="space-between"
                      align="center"
                      p={2}
                      mb={1}
                      borderRadius="md"
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Box minW={0}>
                        <Text fontSize="sm" color="gray.800" noOfLines={1}>
                          {item.patient?.name || 'Unknown Patient'}{' '}
                          <Text as="span" color="gray.500">
                            ({item.ivrId})
                          </Text>
                        </Text>
                        <Text fontSize="xs" color="gray.600" noOfLines={1}>
                          {item.facility?.name || 'Unknown Facility'}
                        </Text>
                      </Box>
                      <Button
                        size="xs"
                        colorScheme="teal"
                        onClick={async () => {
                          try {
                            await bootstrapForIvr(item.ivrId);
                          } catch (err) {
                            toast({
                              title: 'Failed to load IVR',
                              description: err.message,
                              status: 'error',
                              duration: 4000,
                            });
                          }
                        }}
                        _hover={{ transform: 'scale(1)' }}
                      >
                        Use This IVR
                      </Button>
                    </Flex>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Once an IVR is chosen, show the full order builder */}
      {orderReady && (
        <>
          {renderIvrDetails()}

          {(woundGrafts || []).map((wg, i) => {
            const w = wg.woundMeta || {};
            const isRemoved = !!w.isRemoved;
            const woundArea =
              (parseFloat(w.woundLength) || 0) *
              (parseFloat(w.woundWidth) || 0);
            const grafts = wg.addedGrafts || [];
            const totalUnits = grafts.reduce((sum, g) => {
              const [aw, ah] = String(g.size || '')
                .toLowerCase()
                .split('x')
                .map(Number);
              const qty = parseInt(g.quantity);
              if (!isNaN(aw) && !isNaN(ah) && !isNaN(qty))
                return sum + aw * ah * qty;
              return sum;
            }, 0);
            const remaining = woundArea - totalUnits;

            // Build per-wound product roster
            const normalizedProducts = (products || []).map((p) => ({
              id: p.productId,
              name: p.productName,
              qCode: p.qCode,
              sizes: Array.isArray(p.sizes) ? p.sizes : [],
              price: parseFloat(p.price || 0),
              imageUrl: p.imageUrl,
            }));

            const defaultId =
              wg.productId ||
              normalizedProducts.find(
                (p) => p.qCode === ivr.wounds?.[i]?.hcpcs
              )?.id ||
              normalizedProducts[0]?.id;

            return (
              <Box
                key={i}
                p={4}
                borderWidth={1}
                borderRadius="lg"
                my={4}
                bg={isRemoved ? 'red.50' : 'gray.50'}
                opacity={isRemoved ? 0.8 : 1}
              >
                <Flex
                  align="center"
                  justify="space-between"
                  mb={2}
                  gap={3}
                  wrap="wrap"
                >
                  <Box>
                    <Text fontWeight="bold">
                      Wound {i + 1}:{' '}
                      {w.woundType === 'Other' && w.woundTypeOther
                        ? w.woundTypeOther
                        : w.woundType || '—'}
                      {isRemoved && (
                        <Text
                          as="span"
                          color="red.500"
                          fontWeight="semibold"
                        >
                          {' '}
                          (Removed)
                        </Text>
                      )}
                      {w.isNew && !isRemoved && (
                        <Text
                          as="span"
                          color="teal.500"
                          fontWeight="semibold"
                        >
                          {' '}
                          (New)
                        </Text>
                      )}
                    </Text>
                    <Text fontSize="sm">
                      Size: {w.woundLength} x {w.woundWidth} x {w.woundDepth} cm
                      = {woundArea.toFixed(2)} cm²
                    </Text>
                  </Box>
                  <Flex gap={2}>
                    {!isRemoved ? (
                      <Button
                        size="xs"
                        colorScheme="yellow"
                        variant="outline"
                        onClick={() => confirmRemoveWound(i)}
                        _hover={{ transform: 'scale(1)' }}
                      >
                        Remove
                      </Button>
                    ) : (
                      !w.isNew && (
                        <Button
                          size="xs"
                          colorScheme="green"
                          variant="outline"
                          onClick={() => restoreWound(i)}
                          _hover={{ transform: 'scale(1)' }}
                        >
                          Restore
                        </Button>
                      )
                    )}
                  </Flex>
                </Flex>

                {/* Inline wound size editor — hidden when removed */}
                {!isRemoved && (
                  <>
                    <Flex
                      gap={2}
                      align="center"
                      wrap="wrap"
                      mb={3}
                    >
                      <NumberInput
                        value={w.woundLength || ''}
                        min={0}
                        precision={2}
                        step={0.01}
                        width="90px"
                        onChange={(valueString) =>
                          setWoundMeta(i, { woundLength: valueString })
                        }
                      >
                        <NumberInputField placeholder="Length" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text>x</Text>
                      <NumberInput
                        value={w.woundWidth || ''}
                        min={0}
                        precision={2}
                        step={0.01}
                        width="90px"
                        onChange={(valueString) =>
                          setWoundMeta(i, { woundWidth: valueString })
                        }
                      >
                        <NumberInputField placeholder="Width" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text>x</Text>
                      <NumberInput
                        value={w.woundDepth || ''}
                        min={0}
                        precision={2}
                        step={0.01}
                        width="90px"
                        onChange={(valueString) =>
                          setWoundMeta(i, { woundDepth: valueString })
                        }
                      >
                        <NumberInputField placeholder="Depth" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text>cm</Text>

                      <CSelect
                        size="sm"
                        value={w.woundType || ''}
                        onChange={(e) =>
                          setWoundMeta(i, { woundType: e.target.value })
                        }
                        maxW="260px"
                        bg="white"
                      >
                        {[
                          'Diabetic Foot Ulcer',
                          'Venous Leg Ulcer',
                          'Pressure Ulcer',
                          'Traumatic Burns',
                          'Radiation Burns',
                          'Dehisced Surgical Wound',
                          'Necrotizing Faciitis',
                          'Other',
                        ].map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </CSelect>

                      {w.woundType === 'Other' && (
                        <Input
                          size="sm"
                          placeholder="Specify Wound Type"
                          value={w.woundTypeOther || ''}
                          onChange={(e) =>
                            setWoundMeta(i, { woundTypeOther: e.target.value })
                          }
                          maxW="260px"
                          bg="white"
                        />
                      )}
                    </Flex>

                    {/* Coverage bar */}
                    <Flex
                      mt={1}
                      bg="gray.200"
                      h="12px"
                      w="100%"
                      borderRadius="md"
                      overflow="hidden"
                    >
                      <Box
                        h="100%"
                        bg={
                          remaining >= 0
                            ? 'green.400'
                            : Math.abs(remaining) < 2
                            ? 'yellow.300'
                            : Math.abs(remaining) < 3
                            ? 'orange.400'
                            : 'red.500'
                        }
                        width={`${
                          woundArea > 0
                            ? Math.min((totalUnits / woundArea) * 100, 100)
                            : 0
                        }%`}
                        transition="width 0.3s ease"
                      />
                    </Flex>

                    {/* Per-wound graft selector */}
                    <AdminGraftSelectorPro
                      woundIndex={i}
                      woundLength={w.woundLength}
                      woundWidth={w.woundWidth}
                      allProducts={normalizedProducts}
                      allowMixBrands={powerMode}
                      graftsUsed={grafts}
                      selectedProductId={wg.productId || defaultId || ''}
                      setSelectedProductId={(pid) => setWoundProduct(i, pid)}
                      onAddGraft={(g) => addGraft(i, g)}
                      onRemoveGraft={(g) => removeGraft(i, g)}
                    />
                  </>
                )}
              </Box>
            );
          })}

          <Box
            mt={woundGrafts?.length ? 2 : 6}
            mb={6}
          >
            <Flex justify={woundGrafts?.length ? 'right' : 'center'}>
              <Button
                size="sm"
                colorScheme="teal"
                onClick={addWound}
                _hover={{ transform: 'scale(1)' }}
              >
                + Add Wound
              </Button>
            </Flex>
          </Box>

          {/* Order Totals */}
          <Box
            mt={4}
            mb={6}
            p={4}
            borderWidth={1}
            borderRadius="lg"
            bg="white"
          >
            <Heading size="sm" mb={2}>
              Order Totals
            </Heading>
            <Text fontSize="sm" color="gray.600" mb={1}>
              Total Coverage: {orderTotals.totalArea.toFixed(2)} cm²
            </Text>
            <Text fontWeight="semibold">
              Materials Total: {formatter.format(orderTotals.totalList)}
            </Text>
            <Text color="gray.700">
              Expected Payout{' '}
              <Text as="span" color="gray.500">
                (after -40%)
              </Text>
              : <strong>{formatter.format(orderTotals.expectedPayout)}</strong>
            </Text>
          </Box>

          {/* Shipping & Dates */}
          <FormControl mt={6}>
            <FormLabel>Delivery Date</FormLabel>
            <Box maxW="250px">
              <Input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                isRequired
              />
            </Box>
          </FormControl>

          <FormControl mt={6}>
            <FormLabel>Shipping Address</FormLabel>
            {useCustomAddress && (
              <Tag colorScheme="purple" mb={2} maxW="fit-content">
                <TagLabel>Custom Address In Use</TagLabel>
              </Tag>
            )}

            {!useCustomAddress ? (
              <>
                {shippingAddresses.length === 0 ? (
                  <Text>No saved shipping addresses found.</Text>
                ) : (
                  <CSelect
                    value={selectedShippingAddress}
                    onChange={(e) => setSelectedShippingAddress(e.target.value)}
                    maxW="520px"
                    bg="white"
                  >
                    {shippingAddresses.map((addr, idx) => (
                      <option key={idx} value={JSON.stringify(addr)}>
                        {addr.label ||
                          `${addr.street}, ${addr.city}, ${addr.state}`}
                      </option>
                    ))}
                  </CSelect>
                )}
                <Button
                  size="sm"
                  mt={2}
                  onClick={() => setUseCustomAddress(true)}
                  _hover={{ transform: 'scale(1)' }}
                >
                  Use Different Address
                </Button>
              </>
            ) : (
              <>
                <VStack spacing={2} align="stretch" maxW="520px">
                  {['label', 'street', 'city', 'state', 'zip'].map((field) => (
                    <Input
                      key={field}
                      placeholder={
                        field.charAt(0).toUpperCase() + field.slice(1)
                      }
                      value={customAddress[field] || ''}
                      onChange={(e) =>
                        setCustomAddress((prev) => ({
                          ...prev,
                          [field]: e.target.value,
                        }))
                      }
                      bg="white"
                    />
                  ))}
                </VStack>

                <Flex mt={2} gap={2} wrap="wrap">
                  <Button
                    size="sm"
                    onClick={() => setUseCustomAddress(false)}
                    _hover={{ transform: 'scale(1)' }}
                  >
                    Use Saved Address
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    isDisabled
                    title="Functionality Coming Soon"
                    _hover={{ transform: 'scale(1)' }}
                  >
                    Save to User Account
                  </Button>
                </Flex>
              </>
            )}
          </FormControl>

          <FormControl mt={4}>
            <FormLabel>Manual Order Creation Date</FormLabel>
            <Box maxW="250px">
              <Input
                type="date"
                value={overrideCreationDate}
                onChange={(e) => setOverrideCreationDate(e.target.value)}
              />
            </Box>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Used for backdating paper/concierge orders. Defaults to today.
            </Text>
          </FormControl>

          <Button
            colorScheme="green"
            mt={6}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText={
              editOrderId
                ? 'Saving...'
                : cloneOrderId
                ? 'Cloning...'
                : 'Submitting...'
            }
            spinnerPlacement="start"
            isDisabled={isSubmitting}
            _hover={{ transform: 'scale(1)' }}
          >
            {editOrderId
              ? 'Save Order'
              : cloneOrderId
              ? 'Create Cloned Order'
              : 'Submit Order'}
          </Button>
        </>
      )}

      {/* If we’re still bootstrapping an IVR from the route, show a subtle loading state */}
      {!orderReady && isBootstrapping && (
        <Flex justify="center" align="center" minH="120px">
          <Spinner />
        </Flex>
      )}

      {/* Confirm Remove Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Remove Wound</ModalHeader>
          <ModalBody>
            {confirmRemoveIdx !== null && (
              <>
                Are you sure you want to remove{' '}
                <strong>
                  {(woundGrafts[confirmRemoveIdx]?.woundMeta?.woundType ===
                    'Other' &&
                    woundGrafts[confirmRemoveIdx]?.woundMeta
                      ?.woundTypeOther) ||
                    woundGrafts[confirmRemoveIdx]?.woundMeta?.woundType ||
                    'Wound'}
                </strong>{' '}
                with dimensions{' '}
                <strong>
                  {
                    woundGrafts[confirmRemoveIdx]?.woundMeta?.woundLength
                  }{' '}
                  x{' '}
                  {
                    woundGrafts[confirmRemoveIdx]?.woundMeta?.woundWidth
                  }{' '}
                  x{' '}
                  {
                    woundGrafts[confirmRemoveIdx]?.woundMeta?.woundDepth
                  }{' '}
                  cm
                </strong>
                ?
                {woundGrafts[confirmRemoveIdx]?.woundMeta?.isNew && (
                  <Text mt={2} fontSize="sm" color="gray.600">
                    This is a newly added wound and will be deleted from the
                    list.
                  </Text>
                )}
                {!woundGrafts[confirmRemoveIdx]?.woundMeta?.isNew && (
                  <Text mt={2} fontSize="sm" color="gray.600">
                    This is an existing wound and will be marked as removed (you
                    can restore it later).
                  </Text>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onClose}
              mr={3}
              _hover={{ transform: 'scale(1)' }}
            >
              No
            </Button>
            <Button
              colorScheme="red"
              onClick={finalizeRemoveWound}
              _hover={{ transform: 'scale(1)' }}
            >
              Yes, Remove
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminCreateOrderPage;
